import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../api.service';
import { Site } from '../../study.model';
import { CommonService } from '../../shared/common.service';
declare var bootstrap: any;

@Component({
  selector: 'app-site-list',
  templateUrl: './site-list.component.html',
  styleUrls: ['./site-list.component.scss'],
})
export class SiteListComponent implements OnInit {
  cities: string[] = [];
  states: string[] = [];
  countries: string[] = [];

  searchSiteLocation: string = '';
  city: string = '';
  state: string = '';
  country: string = '';

  allSites: Site[] = [];
  sites: Site[] = [];

  selectedSite: Site = {
    id: 0,
    site_id: 0,
    site_name: '',
    site_address: '',
    city: '',
    state: '',
    country: '',
  };

  filteredSites: Site[] = [];

  loading: boolean = false;
  currentPage: number = 1;
  pageSize: number = 5;
  pages: number = 1;
  pageRange: number[] = [];

  // Freeze/Unfreeze properties
  currentSite: Site | null = null;
  isFreezing: boolean = true;
  freezeEmail: string = '';
  freezePassword: string = '';
  freezeReason: string = '';
  freezeError: string = '';
  showFreezePassword: boolean = false;

  // Lock properties
  lockEmail: string = '';
  lockPassword: string = '';
  lockReason: string = '';
  lockError: string = '';
  showLockPassword: boolean = false;

  // User data
  currentUser: any = null;

  constructor(
    private apiService: ApiService,
    private commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.currentUser = this.commonService.getUserData();
    this.loadSites();
  }

  private canManageFreezeLock(): boolean {
    const designation = this.currentUser?.designation || '';
    return designation === 'Monitor' || designation === 'Data Manager';
  }

  // === Load sites and populate dropdowns from site list ===
  loadSites(): void {
    this.loading = true;
    const id = this.commonService.getUserData().id;

    this.apiService.getSites(id).subscribe({
      next: (response: Site[]) => {
        this.allSites = response.sort((a, b) => a.site_id - b.site_id);
        this.filteredSites = [...this.allSites];

        // ✅ Populate dropdowns dynamically from site list
        this.cities = [
          ...new Set(this.allSites.map((s) => s.city).filter((c) => !!c)),
        ];
        this.states = [
          ...new Set(this.allSites.map((s) => s.state).filter((s) => !!s)),
        ];
        this.countries = [
          ...new Set(this.allSites.map((s) => s.country).filter((c) => !!c)),
        ];

        this.updatePagination();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching sites:', err);
        this.loading = false;
      },
    });
  }

  // === Search / Filter ===
  searchSites(): void {
    this.loading = true;
    setTimeout(() => {
      this.filteredSites = this.allSites.filter(
        (site) =>
          (this.searchSiteLocation
            ? site.site_address
                ?.toLowerCase()
                .includes(this.searchSiteLocation.toLowerCase())
            : true) &&
          (this.city ? site.city === this.city : true) &&
          (this.state ? site.state === this.state : true) &&
          (this.country ? site.country === this.country : true)
      );
      this.updatePagination();
      this.loading = false;
    }, 200);
  }

  clear(): void {
    this.searchSiteLocation = '';
    this.city = '';
    this.state = '';
    this.country = '';
    this.filteredSites = [...this.allSites];
    this.updatePagination();
  }

  // === Pagination ===
  updatePagination(): void {
    this.pages = Math.ceil(this.filteredSites.length / this.pageSize) || 1;
    this.currentPage = 1;
    this.setPageData();
    this.pageRange = Array.from({ length: this.pages }, (_, i) => i + 1);
  }

  setPageData(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    this.sites = this.filteredSites.slice(start, end);
  }

  nextPage(): void {
    if (this.currentPage < this.pages) {
      this.currentPage++;
      this.setPageData();
    }
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.setPageData();
    }
  }

  goToPage(page: number): void {
    this.currentPage = page;
    this.setPageData();
  }

  // === Edit Modal ===
  openEditModal(site: Site): void {
    if (site.is_frozen || site.is_locked) {
      alert(
        'This site is ' +
          (site.is_locked ? 'locked' : 'frozen') +
          '. No changes are allowed.'
      );
      return;
    }
    this.selectedSite = { ...site };
    const modal = new (window as any).bootstrap.Modal(
      document.getElementById('editModal')
    );
    modal.show();
  }

  updateSite(): void {
    if (!this.selectedSite || !this.selectedSite.id) return;

    const isDuplicate = this.allSites.some(
      (site) =>
        site.id !== this.selectedSite.id &&
        site.site_name?.toLowerCase().trim() ===
          this.selectedSite.site_name?.toLowerCase().trim() &&
        site.site_address?.toLowerCase().trim() ===
          this.selectedSite.site_address?.toLowerCase().trim()
    );

    if (isDuplicate) {
      alert('A site with the same name and address already exists.');
      return;
    }

    const siteDataToUpdate = {
      site_id: this.selectedSite.site_id, // keep original site_id
      site_name: this.selectedSite.site_name,
      site_address: this.selectedSite.site_address,
      city: this.selectedSite.city,
      state: this.selectedSite.state,
      country: this.selectedSite.country,
    };

    // Use `id` to update the row
    this.apiService
      .updateSite(this.selectedSite.id, siteDataToUpdate)
      .subscribe(() => {
        this.loadSites();
      });

    const modalEl = document.getElementById('editModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();
  }

  // === Delete Site ===
  deleteSite(id: number): void {
    const site = this.allSites.find((s) => s.site_id === id || s.id === id);
    if (site && (site.is_frozen || site.is_locked)) {
      alert(
        'This site is ' +
          (site.is_locked ? 'locked' : 'frozen') +
          '. No changes are allowed.'
      );
      return;
    }
    if (
      confirm(
        'Are you sure you want to delete this site? All related data will be deleted.'
      )
    ) {
      this.apiService.deleteSite(id.toString()).subscribe({
        next: () => this.loadSites(),
        error: (err) => console.error('Delete failed:', err),
      });
    }
  }

  // === Add Location Modal ===
  openAddLocationModal(): void {
    const modal = new (window as any).bootstrap.Modal(
      document.getElementById('addLocationModal')
    );
    modal.show();
  }

  onLocationAdded(): void {
    const modalEl = document.getElementById('addLocationModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();
    this.loadSites();
  }

  // ===========================================
  // FREEZE/UNFREEZE SITE METHODS
  // ===========================================

  freezeSite(site: Site): void {
    if (!this.canManageFreezeLock()) {
      return;
    }
    this.currentSite = site;
    this.isFreezing = !site.is_frozen;
    this.freezeEmail = '';
    this.freezePassword = '';
    this.freezeReason = '';
    this.freezeError = '';
    this.showFreezePassword = false;

    const modalElement = document.getElementById('freezeSiteModal');
    if (modalElement) {
      const bootstrapModal = new bootstrap.Modal(modalElement);
      bootstrapModal.show();
    }
  }

  toggleFreezePasswordVisibility(): void {
    this.showFreezePassword = !this.showFreezePassword;
  }

  authenticateAndFreezeSite(): void {
    if (!this.freezeEmail || !this.freezePassword || !this.freezeReason) {
      this.freezeError = 'Please enter email, password, and reason';
      return;
    }

    if (!this.currentSite) {
      this.freezeError = 'No site selected.';
      return;
    }

    this.freezeError = '';
    this.loading = true;

    const purpose = this.isFreezing ? 'freeze_site' : 'unfreeze_site';

    this.apiService.login(this.freezeEmail, this.freezePassword, purpose).subscribe({
      next: () => this.updateSiteFreezeStatus(),
      error: (error) => {
        this.loading = false;
        this.freezeError = 'Invalid email or password. Please try again.';
        console.error('Authentication error:', error);
      },
    });
  }

  private updateSiteFreezeStatus(): void {
    if (!this.currentSite) {
      this.loading = false;
      return;
    }

    const updateData = {
      site_id: this.currentSite.site_id,
      is_frozen: this.isFreezing,
      reason: this.freezeReason,
    };

    this.apiService.updateSiteFreezeStatus(updateData).subscribe({
      next: () => {
        // Update local state (optimistic) + reload to stay consistent
        const idxAll = this.allSites.findIndex((s) => s.site_id === this.currentSite?.site_id);
        if (idxAll !== -1) this.allSites[idxAll].is_frozen = this.isFreezing;
        const idxFiltered = this.filteredSites.findIndex((s) => s.site_id === this.currentSite?.site_id);
        if (idxFiltered !== -1) this.filteredSites[idxFiltered].is_frozen = this.isFreezing;

        this.loading = false;
        this.closeFreezeSiteModal();
        alert(`Site ${this.isFreezing ? 'frozen' : 'unfrozen'} successfully!`);
        this.loadSites();
      },
      error: (error) => {
        console.error('Error updating site freeze status:', error);
        this.loading = false;
        let errorMessage = 'Failed to update site freeze status. Please try again.';
        if (error?.error) {
          if (error.error.message) errorMessage = error.error.message;
          else if (error.error.error) errorMessage = error.error.error;
          else if (typeof error.error === 'string') errorMessage = error.error;
        }
        this.freezeError = errorMessage;
        alert(errorMessage);
      },
    });
  }

  closeFreezeSiteModal(): void {
    const modalElement = document.getElementById('freezeSiteModal');
    if (modalElement) {
      const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
      bootstrapModal?.hide();
    }
    this.freezeEmail = '';
    this.freezePassword = '';
    this.freezeReason = '';
    this.freezeError = '';
    this.currentSite = null;
  }

  // ===========================================
  // LOCK SITE METHODS (IRREVERSIBLE)
  // ===========================================

  lockSite(site: Site): void {
    if (!this.canManageFreezeLock()) {
      return;
    }
    if (site.is_locked) {
      alert('This site is already locked and cannot be unlocked.');
      return;
    }

    this.currentSite = site;
    this.lockEmail = '';
    this.lockPassword = '';
    this.lockReason = '';
    this.lockError = '';
    this.showLockPassword = false;

    const modalElement = document.getElementById('lockSiteModal');
    if (modalElement) {
      const bootstrapModal = new bootstrap.Modal(modalElement);
      bootstrapModal.show();
    }
  }

  toggleLockPasswordVisibility(): void {
    this.showLockPassword = !this.showLockPassword;
  }

  authenticateAndLockSite(): void {
    if (!this.lockEmail || !this.lockPassword || !this.lockReason) {
      this.lockError = 'Please enter email, password, and reason';
      return;
    }

    if (!this.currentSite) {
      this.lockError = 'No site selected.';
      return;
    }

    this.lockError = '';
    this.loading = true;

    const purpose = 'lock_site';

    this.apiService.login(this.lockEmail, this.lockPassword, purpose).subscribe({
      next: () => this.updateSiteLockStatus(),
      error: (error) => {
        this.loading = false;
        this.lockError = 'Invalid email or password. Please try again.';
        console.error('Authentication error:', error);
      },
    });
  }

  private updateSiteLockStatus(): void {
    if (!this.currentSite) {
      this.loading = false;
      return;
    }

    const updateData = {
      site_id: this.currentSite.site_id,
      is_locked: true,
      reason: this.lockReason,
    };

    this.apiService.updateSiteLockStatus(updateData).subscribe({
      next: () => {
        const idxAll = this.allSites.findIndex((s) => s.site_id === this.currentSite?.site_id);
        if (idxAll !== -1) this.allSites[idxAll].is_locked = true;
        const idxFiltered = this.filteredSites.findIndex((s) => s.site_id === this.currentSite?.site_id);
        if (idxFiltered !== -1) this.filteredSites[idxFiltered].is_locked = true;

        this.loading = false;
        this.closeLockSiteModal();
        alert('Site locked successfully! This action cannot be undone.');
        this.loadSites();
      },
      error: (error) => {
        console.error('Error updating site lock status:', error);
        this.loading = false;
        let errorMessage = 'Failed to lock site. Please try again.';
        if (error?.error) {
          if (error.error.message) errorMessage = error.error.message;
          else if (error.error.error) errorMessage = error.error.error;
          else if (typeof error.error === 'string') errorMessage = error.error;
        }
        this.lockError = errorMessage;
        alert(errorMessage);
      },
    });
  }

  closeLockSiteModal(): void {
    const modalElement = document.getElementById('lockSiteModal');
    if (modalElement) {
      const bootstrapModal = bootstrap.Modal.getInstance(modalElement);
      bootstrapModal?.hide();
    }
    this.lockEmail = '';
    this.lockPassword = '';
    this.lockReason = '';
    this.lockError = '';
    this.currentSite = null;
  }
}
